// Shared by the SPA signup page and the API (MCP authorize page), so the
// returnTo allow-list cannot drift apart.
//
// /signup works in every environment. The app's navigation links to it only on
// staging; elsewhere it is reached by direct link, or from the MCP authorize
// page's "Create your restaurant" button.

/** The MCP OAuth consent endpoint — the only place signup may send a user back to. */
export const MCP_AUTHORIZE_PATH = "/api/mcp/oauth/authorize";

/**
 * The /signup link for an authorize request, carrying it as a relative
 * returnTo so the user lands back on the consent screen after creating their
 * restaurant.
 */
export const buildSignupUrl = (authorizeUrl: string) => {
  const url = new URL(authorizeUrl);
  return `/signup?returnTo=${encodeURIComponent(url.pathname + url.search)}`;
};

/**
 * Validate a returnTo value. Only a same-origin path to the MCP authorize
 * endpoint is accepted; anything else returns null, so signup can never be used
 * as an open redirect.
 */
export const safeSignupReturnTo = (
  raw: unknown,
  origin: string,
): string | null => {
  if (typeof raw !== "string" || !raw.startsWith("/")) return null;
  // Protocol-relative ("//evil.example") and backslash variants that browsers
  // normalise to one.
  if (raw.startsWith("//") || raw.startsWith("/\\")) return null;

  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin || url.pathname !== MCP_AUTHORIZE_PATH) return null;
  return url.pathname + url.search;
};
